import type { QuestionnaireSummaryApi } from "@/Modules/Models/Api/Questionnaire";
import type { QuestionnaireSummary } from "@/Modules/Models/Domain/Questionnaire";
import type { QuestionnaireStore } from "../Store";
import type { QuestionnaireSummaryTransformerType } from "@/Modules/Transformers/Questionaire/QuestionnaireSummary";

import { QuestionnaireType } from "@/Modules/Models/Domain/Questionnaire";

import QuestionnairesApiConnector from "@/Services/TherapyApi/Questionnaires";
import QuestionnaireSummaryTransformer from "@/Modules/Transformers/Questionaire/QuestionnaireSummary";

import { Role } from "@/Common/Models/Domain/Role";

import { hasValue } from "@/Utils/Objects";
import { useUserStore } from "@/Modules/User/Store";

import { danger } from "@/Utils/Debug";

class FetchQuestionnaireSummariesActionHandler {
  constructor(
    private readonly questionnaireStore: QuestionnaireStore,
    private readonly userStore = useUserStore(),
    private readonly questionnairesApiConnector = new QuestionnairesApiConnector(),
    private readonly questionnaireSummaryTransformer = QuestionnaireSummaryTransformer
  ) {}

  public async handle(): Promise<void> {
    this.questionnaireStore.isLoading = true;

    if (hasValue(this.userStore.user)) {
      try {
        const questionnaireSummaryApiResponse =
          await this.questionnairesApiConnector.getQuestionnairesByUserId(this.userStore.user.id, {
            page_size: 1000,
          });

        const questionnaireSummaries = questionnaireSummaryApiResponse.body.map((q) =>
          this.questionnaireSummaryTransformer.toDomain(q)
        );
        let filteredQuestionnaireSummaries: QuestionnaireSummary[] = [];

        if (this.userStore.user.role === Role.CUSTOMER) {
          filteredQuestionnaireSummaries = this.filterQuestionnaireSummaries(questionnaireSummaries);
          this.questionnaireStore.deleteAndUnsubscribeQuestionnaires = filteredQuestionnaireSummaries.filter(
            (quiz) => quiz.type === QuestionnaireType.CUSTOMER_ABOUT_DELETE
          );
        } else {
          this.questionnaireStore.questionnaires.forEach((questionnaire) => {
            const questionnaireSummary = questionnaireSummaries.find(
              (questionnaireSummary) => questionnaireSummary.questionnaireId === questionnaire.id
            );

            if (hasValue(questionnaireSummary)) {
              filteredQuestionnaireSummaries.push(questionnaireSummary);
            }
          });
        }

        this.questionnaireStore.questionnaireSummaries = filteredQuestionnaireSummaries;
      } catch (err) {
        danger(err);
      }
    }

    this.questionnaireStore.isLoading = false;
  }

  private transformQuestionnaireSummaryApiToQuestionnaire(
    questionnaireSummaryApi: QuestionnaireSummaryApi[],
    transformer: QuestionnaireSummaryTransformerType
  ): QuestionnaireSummary[] {
    const questionnairesSummary: QuestionnaireSummary[] = questionnaireSummaryApi.map(
      (questionnaireSummaryApi) => {
        return transformer.toDomain(questionnaireSummaryApi);
      }
    );

    return questionnairesSummary;
  }

  private filterQuestionnaireSummaries(
    questionnaireSummaries: QuestionnaireSummary[]
  ): QuestionnaireSummary[] {
    const allowedQuestionnaireTypes = [
      QuestionnaireType.CUSTOMER_ABOUT_HIMSELF,
      QuestionnaireType.CUSTOMER_ABOUT_THERAPIST,
      QuestionnaireType.CUSTOMER_ABOUT_DELETE,
    ];

    const uniqueQuestionnaireIds: string[] = [];

    const filteredQuestionnaireSummaries = questionnaireSummaries.filter((questionnaireSummary) => {
      const isAllowed = allowedQuestionnaireTypes.includes(questionnaireSummary.type);

      if (isAllowed) {
        uniqueQuestionnaireIds.push(questionnaireSummary.questionnaireId);

        return questionnaireSummary;
      }
    });

    return filteredQuestionnaireSummaries;
  }
}

export default FetchQuestionnaireSummariesActionHandler;
